import { useDatadog } from '@/auth/datadog';
import { useSentry } from '@/auth/sentry';
import { ApolloProvider } from '@/utils/apollo';
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as JotaiProvider } from 'jotai';
import { Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { LoadingBarContainer } from 'react-top-loading-bar';

import { RootNavOffsetStyles } from './components/layout/rootNavOffset';
import { AxleTableGlobalStyles } from './components/tables/theming';
import { AXLE_TEAL } from './constants/colors';
import { DialogProvider } from './dialogs/DialogManager';
import { FeatureFlagOverrideMount } from './featureFlags/FeatureFlagOverrideMount';
import { AxleErrorBoundary, ErrorFallback, GlobalErrorBoundary } from './fetching/errorBoundaries';
import { queryClient } from './fetching/queryClient';
import { RootSuspenseFallback } from './fetching/rootSuspenseFallback';
import { AxleSuspense } from './fetching/suspense';
import { ReactQueryTopLoadingBar } from './fetching/topLoadingBar';
import { RootRoutes } from './routes';
import { AxleTheme } from './theme';
import { AxleToastContainer } from './toast';
import { envVars } from './utils/envVars';
import { appStore } from './utils/jotaiStore';

const enableReactQueryDevTools = envVars.enableReactQueryDevTools;

/* Rendered inside the data router — everything here has access to router context. */
function AppRouterContent() {
    return (
        <Suspense fallback={<RootSuspenseFallback />}>
            {/* TODO(scroll-restoration): now that the layout is flat and the body is the
             *  scroll container, POP (back/forward) gets native scroll restoration, but
             *  PUSH navigations no longer reset scroll to the top. Render the React Router
             *  <ScrollRestoration /> here to handle PUSH reset + POP restore. */}
            <LoadingBarContainer props={{ color: AXLE_TEAL, height: 3 }}>
                <ReactQueryTopLoadingBar />
                {/* Single persistent instance that reserves the #root offset for the nav
                    drawer / app bar. Kept outside the route/feature-flag Suspense so it never
                    remounts across navigations. */}
                <RootNavOffsetStyles />
                <DialogProvider>
                    <AxleSuspense customFallback={<RootSuspenseFallback />}>
                        <RootRoutes />
                    </AxleSuspense>
                </DialogProvider>
            </LoadingBarContainer>
            <AxleToastContainer />
        </Suspense>
    );
}

// The data router catches route-level errors (lazy-chunk/CSS load failures,
//  route transitions) and, with no errorElement, renders React Router's default
//  "Unexpected Application Error" screen.
//  So... instead we point it at our ErrorFallback so those errors show the same screen
// they did under BrowserRouter before the v7 migration.
const router = createBrowserRouter([
    { path: '*', element: <AppRouterContent />, errorElement: <ErrorFallback /> },
]);

/* Define all the app-level components, providers, etc. */
export function App() {
    useSentry();
    useDatadog();

    return (
        <>
            <GlobalErrorBoundary>
                {/* Make Jotai use our custom Query client. See: https://jotai.org/docs/integrations/query#referencing-the-same-instance-of-query-client-in-your-project */}
                {/* TODO: Set up Provider to rehydrate using the method linked above, as the current version of Jotai does not accept our previous initialValues implementation. */}
                {/* The explicit `store` prop keeps React hooks and non-React code
                 *  (Apollo error link, axios interceptor) on the same store. See jotaiStore.ts. */}
                <JotaiProvider store={appStore}>
                    <ApolloProvider>
                        {/* This is the global error boundary. It catches errors and show an error screen instead.
                         *   Without this, React would unmount the whole component tree and show a blank screen.
                         *
                         * TRY TO HANDLE ERRORS LOCALLY! It's a very bad user experience to see a crash screen. Instead,
                         *  try to handle errors locally. Use an <ErrorBoundary> to wrap your component, and display
                         *  an appropriate fallback there. Send a toast. Etc. Try to leave the page functional. This
                         *  is just a panic screen of last resort.
                         *
                         * Note: This doesn't catch errors in async code by default. Async code should be inside
                         *  useQuery or atomWithQuery, which handle errors correctly. Otherwise, your error will
                         *  pass silently.
                         *
                         * This should be the top-level component so it can catch everything inside.
                         */}
                        <AxleErrorBoundary clearAuth={true} reload={true}>
                            {/* Inject Emotion before JSS; needed for upgrade from MUI v4 to v5.
                             *  see: https://mui.com/material-ui/migration/v5-style-changes/#%E2%9C%85-adjust-css-injection-order
                             */}
                            <StyledEngineProvider injectFirst>
                                {/* Provide app-wide Material UI theme. */}
                                <ThemeProvider theme={AxleTheme}>
                                    {/* Kind of like normalize.css, and does things like apply our background color. */}
                                    <CssBaseline />
                                    {/* App-wide base styles previously in index.css: body/code
                                        fonts, datepicker popper z-index, and the reduced-motion
                                        reset (WCAG 2.3.3). */}
                                    <GlobalStyles
                                        styles={{
                                            // Give the app shell a definite height so a page that
                                            // fills the viewport can size itself with height: 100%
                                            // — the #root content box already excludes the nav
                                            // gutter reserved as padding. Taller pages still
                                            // overflow onto the body, which keeps the document the
                                            // scroll container.
                                            'html, body, #root': {
                                                height: '100%',
                                            },
                                            body: {
                                                margin: 0,
                                                fontFamily:
                                                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                                                WebkitFontSmoothing: 'antialiased',
                                                MozOsxFontSmoothing: 'grayscale',
                                            },
                                            code: {
                                                fontFamily:
                                                    "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
                                            },
                                            '.react-datepicker-popper, .SingleDatePicker_picker': {
                                                zIndex: '4 !important',
                                            },
                                            // Hide a LinearProgress rendered as a sibling of an
                                            // AxleDataGrid wrapper; the grid shows its own loading
                                            // overlay instead.
                                            ':has(> .axle-data-grid-wrapper) > .MuiLinearProgress-root':
                                                {
                                                    display: 'none',
                                                },
                                            '@media (prefers-reduced-motion: reduce)': {
                                                '*, *::before, *::after': {
                                                    animationDuration: '0.01ms !important',
                                                    animationIterationCount: '1 !important',
                                                    transitionDuration: '0.01ms !important',
                                                    scrollBehavior: 'auto !important',
                                                },
                                            },
                                        }}
                                    />
                                    {/* Selector-scoped overrides for the data table's own
                                        markup. Rendered here rather than inside the table so the
                                        rule set is owned by a component that never unmounts. */}
                                    <AxleTableGlobalStyles />
                                    {/* Tools for reading and forcing feature flag values.
                                        Renders nothing, and loads no chunk, in an environment
                                        whose flag values are the ones people depend on. */}
                                    <FeatureFlagOverrideMount />
                                    {/* Custom react-query client. */}
                                    <QueryClientProvider client={queryClient}>
                                        {/* This includes a floating React Query dev tools button on the page during development. */}
                                        {enableReactQueryDevTools && (
                                            <ReactQueryDevtools
                                                initialIsOpen={false}
                                                position={'top-right'}
                                            />
                                        )}

                                        {/* Helmet is used for managing the document head. It is a simple React wrapper around the HTML head tag. */}
                                        <HelmetProvider>
                                            {/* createBrowserRouter (data router) enables useBlocker and other data APIs. */}
                                            <RouterProvider router={router} />
                                        </HelmetProvider>
                                    </QueryClientProvider>
                                </ThemeProvider>
                            </StyledEngineProvider>
                        </AxleErrorBoundary>
                    </ApolloProvider>
                </JotaiProvider>
            </GlobalErrorBoundary>
        </>
    );
}
